import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, getBusinessId, setStoredBusinessId } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [businessId, setBusinessId] = useState(getBusinessId());
  const [business, setBusiness] = useState(null);

  const applyMe = useCallback((data) => {
    setUser(data.user);
    setMemberships(data.memberships);
    const stored = getBusinessId();
    const pick = data.memberships.find((m) => m.business_id === stored) || data.memberships[0];
    const id = pick ? pick.business_id : null;
    setStoredBusinessId(id);
    setBusinessId(id);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      applyMe(data);
      return data;
    } catch {
      setUser(false);
      setMemberships([]);
      return null;
    }
  }, [applyMe]);

  const loadBusiness = useCallback(async () => {
    if (!businessId) { setBusiness(null); return null; }
    try {
      const { data } = await api.get("/business");
      setBusiness(data);
      return data;
    } catch {
      setBusiness(null);
      return null;
    }
  }, [businessId]);

  useEffect(() => {
    if (window.location.pathname === "/auth/google") { setUser(false); return; }
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (user) { loadBusiness(); }
  }, [user, businessId, loadBusiness]);

  const switchBusiness = (id) => {
    setStoredBusinessId(id);
    setBusinessId(id);
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setStoredBusinessId(null);
    setBusinessId(null);
    setBusiness(null);
    setMemberships([]);
    setUser(false);
  };

  const membership = memberships.find((m) => m.business_id === businessId) || null;

  return (
    <AuthContext.Provider value={{ user, memberships, membership, businessId, business, setBusiness, applyMe, refresh, loadBusiness, switchBusiness, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
